// The links index: every link a pull request's conversation mentions, one
// entry per target, grouped by kind and site, each with the places that
// mention it (see docs/design.md, "Links index").
//
// Links are read from the same sanitized HTML the page renders (`bodyHtml`),
// so a link the page would not show is not indexed either. Nothing here
// fetches a target.
import type { PullRequestData, ReferencedNode, RelatedRefs, ReviewThread, TimelineItem } from '../api/queries';
import { bodyHtml, splitSuggestionHtml } from './markdown';
import { parseOffDiff } from './offdiff';
import type { ThreadLocation } from './headerTargets';
import { prPath } from '../router';
import { commitPath } from './snapshotLink';
import { LruMap } from '../api/cache';

// ---------------------------------------------------------------- types

/** What a link points at. Owner and repository names keep the case first seen; they compare without it. */
export type LinkTarget =
  /** An issue or pull request; `type` null when the mention does not say which (`#12`). */
  | { kind: 'ref'; owner: string; repo: string; number: number; type: 'pull' | 'issue' | null }
  /** A commit, by its full SHA when it is one of the pull request's. */
  | { kind: 'commit'; owner: string; repo: string; sha: string }
  /** An http(s) URL that is none of the above; `site` is its host without `www.`. */
  | { kind: 'web'; url: string; site: string }
  /** `url` is the link as written (normalized by `safeHref`); `address` is only for showing and matching. */
  | { kind: 'mail'; url: string; address: string };

/** Where a link was mentioned. */
export type Place =
  | { kind: 'description' }
  /** A conversation comment, by node ID. */
  | { kind: 'comment'; id: string }
  /** A review's summary, by node ID. */
  | { kind: 'review'; id: string }
  /** A comment (`comment`, by node ID) in a review thread; `reply` when it is not the thread's first. */
  | { kind: 'thread'; thread: ThreadLocation; comment: string; reply: boolean };

export interface Mention {
  place: Place;
  author: string;
  at: string;
}

/** One place's text: its sanitized HTML. */
export interface LinkSource extends Mention {
  html: string;
  /**
   * The place and its text, unrendered: two sources with the same key have
   * the same HTML, so a `LinkMemo` reads it once. Absent: never memoized.
   */
  key?: string;
}

/** What following a link does: a changelist route, or a page elsewhere (a new tab). */
export type Open = { kind: 'app'; path: string } | { kind: 'external'; url: string };

export interface LinkEntry {
  key: string;
  target: LinkTarget;
  label: string;
  title: string | null;
  open: Open;
  /** In the order of the sources. */
  mentions: Mention[];
}

export interface LinkGroup {
  key: string;
  label: string;
  entries: LinkEntry[];
}

/** An issue or pull request whose title is known without asking GitHub. */
export interface KnownRef {
  owner: string;
  repo: string;
  number: number;
  type: 'pull' | 'issue';
  title: string;
}

export interface LinkContext {
  pr: { owner: string; repo: string; number: number };
  /** The GitHub web URL changelist reads from (github.com, or a GitHub Enterprise Server). */
  githubWebUrl: string;
  /** The pull request's commits (its snapshots), with their headlines where known. */
  commits: readonly { sha: string; title: string | null }[];
  known: readonly KnownRef[];
}

// ---------------------------------------------------------------- hrefs

const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

/**
 * `href` normalized when it is an absolute http, https or mailto URL, else
 * null. The one gate a link from untrusted text passes before it is shown.
 */
export function safeHref(href: string | null | undefined): string | null {
  if (!href) return null;
  try {
    const u = new URL(href);
    return SAFE_SCHEMES.has(u.protocol) ? u.href : null;
  } catch {
    return null;
  }
}

const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
const NAME = /^[A-Za-z0-9_.-]+$/;
/** An owner or repository name: GitHub's characters, but never `.` or `..` (a path step, not a name). */
const isName = (s: string | undefined): s is string => !!s && NAME.test(s) && s !== '.' && s !== '..';
const HEX = /^[0-9a-f]{7,40}$/i;

function webBase(ctx: LinkContext): URL | null {
  try {
    return new URL(ctx.githubWebUrl);
  } catch {
    return null;
  }
}

/** The full SHA of the pull request's commit `sha` abbreviates, if it is one. */
function prCommit(sha: string, ctx: LinkContext): string | null {
  const s = sha.toLowerCase();
  return ctx.commits.find((c) => c.sha.toLowerCase().startsWith(s))?.sha.toLowerCase() ?? null;
}

function commit(owner: string, repo: string, sha: string, ctx: LinkContext): LinkTarget {
  const own = same(owner, ctx.pr.owner) && same(repo, ctx.pr.repo);
  return { kind: 'commit', owner, repo, sha: (own && prCommit(sha, ctx)) || sha.toLowerCase() };
}

/** A GitHub URL's issue, pull request or commit; null for any other page. */
function githubTarget(u: URL, ctx: LinkContext): LinkTarget | null {
  const base = webBase(ctx);
  if (!base || !same(u.host, base.host)) return null;
  const prefix = base.pathname.replace(/\/+$/, '');
  if (prefix && !u.pathname.startsWith(prefix + '/')) return null;
  const [owner, repo, kind, id, sub, sha] = u.pathname.slice(prefix.length).split('/').slice(1);
  if (!isName(owner) || !isName(repo)) return null;
  if (kind === 'commit' && id && HEX.test(id)) return commit(owner, repo, id, ctx);
  if ((kind === 'pull' || kind === 'issues') && /^\d{1,9}$/.test(id ?? '')) {
    if (kind === 'pull' && (sub === 'commits' || sub === 'files' || sub === 'changes') && sha && HEX.test(sha)) return commit(owner, repo, sha, ctx);
    return { kind: 'ref', owner, repo, number: Number(id), type: kind === 'pull' ? 'pull' : 'issue' };
  }
  return null;
}

/** An `@login` or `@org/team` link GitHub renders for a mention: a person, not a link. */
function isMention(a: Element, u: URL, ctx: LinkContext): boolean {
  const base = webBase(ctx);
  return !!base && same(u.host, base.host) && (a.textContent ?? '').trim().startsWith('@');
}

function hrefTarget(a: Element, ctx: LinkContext): LinkTarget | null {
  const href = safeHref(a.getAttribute('href'));
  if (!href) return null;
  const u = new URL(href);
  if (u.protocol === 'mailto:') {
    let address = u.pathname;
    try {
      address = decodeURIComponent(address);
    } catch {
      // A malformed escape: show it as written.
    }
    return address ? { kind: 'mail', url: href, address } : null;
  }
  if (isMention(a, u, ctx)) return null;
  return githubTarget(u, ctx) ?? { kind: 'web', url: href, site: u.hostname.replace(/^www\./, '') };
}

// ---------------------------------------------------------------- text

/** `#12` and `owner/repo#12`, not inside a word, path or `##`. */
const REF = /(?<![\w/#.-])(?:([A-Za-z0-9][A-Za-z0-9-]{0,38})\/([A-Za-z0-9_.-]{1,100}))?#(\d{1,9})\b/g;
/** A hex word that may be a commit SHA. */
const SHA = /(?<![\w@/.-])[0-9a-f]{7,40}(?![\w-])/gi;

/**
 * References in plain text: `#12`, `owner/repo#12`, and commit SHAs. A SHA
 * counts when it abbreviates one of the pull request's commits or is a full
 * 40-digit SHA (taken to be in this repository); any other hex word is too
 * likely to be something else, and nothing is asked of GitHub to find out.
 */
function textTargets(text: string, ctx: LinkContext): { at: number; target: LinkTarget }[] {
  const out: { at: number; target: LinkTarget }[] = [];
  for (const m of text.matchAll(REF)) {
    const [, owner, repo, n] = m;
    if (repo !== undefined && !isName(repo)) continue;
    out.push({ at: m.index, target: { kind: 'ref', owner: owner ?? ctx.pr.owner, repo: repo ?? ctx.pr.repo, number: Number(n), type: null } });
  }
  for (const m of text.matchAll(SHA)) {
    const sha = m[0];
    if (!/[a-f]/i.test(sha) || !/\d/.test(sha)) continue;
    if (sha.length === 40 || prCommit(sha, ctx)) out.push({ at: m.index, target: commit(ctx.pr.owner, ctx.pr.repo, sha, ctx) });
  }
  return out.sort((a, b) => a.at - b.at);
}

/** Where an image in sanitized HTML came from: its `src`, or the URL a "Load image" stand-in holds (markdown.ts `deferImages`). */
const IMAGE_SOURCES: [selector: string, attr: string][] = [
  ['img', 'src'],
  ['button.ext-img', 'data-src'],
];

/**
 * Whether a link and an image name the same picture. On GitHub's user
 * content hosts (`*.githubusercontent.com`) the query is a signature (a
 * `jwt` for private images) that GitHub issues separately for the link and
 * the image, so only origin and path are compared there; elsewhere the
 * whole URL is.
 */
function samePicture(href: string, src: string): boolean {
  if (href === src) return true;
  const a = new URL(href);
  const b = new URL(src);
  return a.hostname.endsWith('.githubusercontent.com') && a.origin === b.origin && a.pathname === b.pathname;
}

/**
 * An anchor that only shows an image and links to that image: GitHub's
 * rendering of every image in a comment (`<a href=X><img src=X></a>`). It
 * is an image, not a link. An image linking elsewhere (a badge) is a link.
 * The sanitizer drops every `data-*` attribute GitHub writes (such as
 * `data-canonical-src`); GitHub points the anchor at the same URL as the
 * image's `src`, which is compared instead.
 */
function isImageWrapper(a: Element): boolean {
  for (const node of a.childNodes) {
    if (node.nodeType === 3 /* text */ && node.textContent?.trim()) return false;
    if (node.nodeType !== 1) continue;
    const el = node as Element;
    // The stand-in's own label ("Load image from …") is not the anchor's text.
    if (el.matches('button.ext-img')) continue;
    if (!['IMG', 'PICTURE'].includes(el.tagName) || el.textContent?.trim()) return false;
  }
  const href = safeHref(a.getAttribute('href'));
  if (!href) return false;
  const sources = IMAGE_SOURCES.flatMap(([sel, attr]) => [...a.querySelectorAll(sel)].map((el) => safeHref(el.getAttribute(attr))));
  return sources.some((src) => src !== null && samePicture(href, src));
}

/** Elements whose contents are never read for links: code, and what cannot hold a link. */
const SKIP = new Set(['CODE', 'PRE', 'KBD', 'SAMP', 'SCRIPT', 'STYLE', 'TEMPLATE', 'BUTTON']);

/**
 * The link targets in sanitized HTML, in document order (with repeats):
 * anchors (http, https and mailto only; GitHub's mention links are
 * skipped), and references and SHAs in the text outside anchors. Nothing
 * inside code is read. Images are not links, nor is an anchor around an
 * image that links to the image itself; one linking elsewhere is.
 * The HTML is parsed into an inert template, which loads nothing.
 */
export function extractLinks(html: string, ctx: LinkContext): LinkTarget[] {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const out: LinkTarget[] = [];
  const walk = (node: Node) => {
    for (const child of node.childNodes) {
      if (child.nodeType === 3 /* text */) {
        for (const t of textTargets(child.textContent ?? '', ctx)) out.push(t.target);
      } else if (child.nodeType === 1 /* element */) {
        const el = child as Element;
        if (SKIP.has(el.tagName)) continue;
        if (el.tagName === 'A') {
          if (isImageWrapper(el)) continue;
          const t = hrefTarget(el, ctx);
          if (t) out.push(t);
          continue;
        }
        walk(el);
      }
    }
  };
  walk(tpl.content);
  return out;
}

/**
 * The issue or pull request a rendered anchor links to, keyed as the Links
 * card keys its entry, and where following it goes; null for any other
 * link (and for an image's own link).
 */
export function anchorRef(a: Element, ctx: LinkContext): { key: string; target: Extract<LinkTarget, { kind: 'ref' }>; open: Open } | null {
  if (isImageWrapper(a)) return null;
  const t = hrefTarget(a, ctx);
  return t?.kind === 'ref' ? { key: targetKey(t, ctx), target: t, open: open(t, ctx) } : null;
}

// ---------------------------------------------------------------- sources

const login = (a: { login: string } | null | undefined) => a?.login ?? 'ghost';

/** A source whose HTML is rendered when first read (a memoized source never is). */
function source(mention: Mention, key: string, render: () => string): LinkSource {
  let html: string | null = null;
  return {
    ...mention,
    key,
    get html() {
      return (html ??= render());
    },
  };
}

/**
 * A review comment's HTML as the page shows its prose: GitHub's rendering
 * without its suggested-change blocks (their code is not prose), or the
 * Markdown when those blocks cannot be cut out.
 */
function threadCommentHtml(c: { body: string; bodyHTML: string }): string {
  const html = c.bodyHTML?.trim() ? c.bodyHTML : '';
  if (!html.includes('js-suggested-changes-blob')) return bodyHtml(html, c.body);
  const cut = splitSuggestionHtml(html);
  return cut ? bodyHtml(cut.join(''), null) : bodyHtml(null, c.body);
}

/**
 * Every place in a pull request's conversation that can mention a link,
 * with its sanitized HTML: the description, conversation comments, review
 * summaries, and each published comment of each review thread (pending
 * ones are the viewer's drafts). An off-diff comment contributes only its
 * own text, as the page shows it: its header's link is changelist's.
 */
export function linkSources(
  pr: Pick<PullRequestData, 'body' | 'bodyHTML' | 'author' | 'createdAt'>,
  timeline: readonly TimelineItem[],
  threads: readonly ReviewThread[],
): LinkSource[] {
  const out: LinkSource[] = [];
  const text = (where: string, t: { body: string; bodyHTML: string }) => `${where}\0${t.bodyHTML ?? ''}\0${t.body}`;
  if (pr.body?.trim())
    out.push(source({ place: { kind: 'description' }, author: login(pr.author), at: pr.createdAt }, text('description', pr), () => bodyHtml(pr.bodyHTML, pr.body)));
  for (const it of timeline) {
    if (it.__typename === 'IssueComment') {
      out.push(
        source({ place: { kind: 'comment', id: it.id }, author: login(it.author), at: it.createdAt }, text(`comment:${it.id}`, it), () => {
          const anchored = parseOffDiff(it.body);
          return anchored ? bodyHtml(null, anchored.text) : bodyHtml(it.bodyHTML, it.body);
        }),
      );
    } else if (it.__typename === 'PullRequestReview' && it.state !== 'PENDING' && it.body.trim()) {
      out.push(
        source({ place: { kind: 'review', id: it.id }, author: login(it.author), at: it.submittedAt ?? it.createdAt }, text(`review:${it.id}`, it), () =>
          bodyHtml(it.bodyHTML, it.body),
        ),
      );
    }
  }
  for (const t of threads) {
    const location: ThreadLocation = { id: t.id, path: t.path, line: t.line, diffSide: t.diffSide, subjectType: t.subjectType };
    t.comments.nodes.forEach((c, i) => {
      if (c.pullRequestReview?.state === 'PENDING') return;
      out.push(
        source(
          { place: { kind: 'thread', thread: location, comment: c.id, reply: i > 0 }, author: login(c.author), at: c.createdAt },
          text(`thread:${c.id}:${i === 0 ? 'first' : 'reply'}`, c),
          () => {
            const anchored = i === 0 ? parseOffDiff(c.body) : null;
            return anchored ? bodyHtml(null, anchored.text) : threadCommentHtml(c);
          },
        ),
      );
    });
  }
  return out;
}

/** Titles GitHub sent for a pull request's related issues: those it closes, and issues and pull requests related to it. */
export function knownRefs(pr: Partial<RelatedRefs>): KnownRef[] {
  const nodes: (ReferencedNode | null | undefined)[] = [
    ...(pr.closingIssuesReferences?.nodes ?? []),
    ...(pr.references?.nodes ?? []).map((e) => (!e ? null : e.__typename === 'CrossReferencedEvent' ? e.source : e.subject)),
  ];
  const out: KnownRef[] = [];
  for (const n of nodes) {
    if (!n || (n.__typename !== 'Issue' && n.__typename !== 'PullRequest') || !n.repository) continue;
    const [owner, repo] = n.repository.nameWithOwner.split('/');
    if (!owner || !repo) continue;
    out.push({ owner, repo, number: n.number, type: n.__typename === 'PullRequest' ? 'pull' : 'issue', title: n.title });
  }
  return out;
}

// ---------------------------------------------------------------- the index

/**
 * The targets read from each source, by its key and what reading depends
 * on (the GitHub host, the pull request, its commits), so a live update
 * re-renders only the sources that changed. Bounded: the least recently
 * used are forgotten.
 */
export class LinkMemo {
  private map = new LruMap<string, LinkTarget[]>(2000);

  /** `read()` for `source`, or what it gave before with the same key and context. */
  targets(source: LinkSource, ctx: LinkContext, read: () => LinkTarget[]): LinkTarget[] {
    if (source.key === undefined) return read();
    const key = `${ctx.githubWebUrl}\0${ctx.pr.owner}/${ctx.pr.repo}#${ctx.pr.number}\0${ctx.commits.map((c) => c.sha).join(',')}\0${source.key}`;
    let t = this.map.get(key);
    if (!t) this.map.set(key, (t = read()));
    return t;
  }
}

/** A web page's URL without the trailing slash of its path (`URL` has already lowercased its host). */
function pageKey(url: string): string {
  const u = new URL(url);
  u.pathname = u.pathname.replace(/\/+$/, '');
  return u.href.replace(/\/(?=[?#]|$)/, '');
}

/** The key of the Links card's entry for web page `url` (an http(s) URL). */
export function webKey(url: string): string {
  return `web:${pageKey(url)}`;
}

/**
 * What makes two targets one entry. A commit of the pull request is keyed
 * by its full SHA; any other by its first 7 digits, the most a mention is
 * sure to give, so `abc1234` and `abc1234def…` are one commit.
 */
function targetKey(t: LinkTarget, ctx: LinkContext): string {
  switch (t.kind) {
    case 'ref':
      return `ref:${t.owner}/${t.repo}#${t.number}`.toLowerCase();
    case 'commit': {
      const own = inThisRepo(t, ctx) && ctx.commits.some((c) => same(c.sha, t.sha));
      return `commit:${t.owner}/${t.repo}@${own ? t.sha : t.sha.slice(0, 7)}`.toLowerCase();
    }
    case 'web':
      return `web:${pageKey(t.url)}`;
    case 'mail':
      return `mail:${t.address.toLowerCase()}`;
  }
}

/** `sites`: every site linked only once, so a long tail of sites is one group. */
type GroupKey = 'repo' | 'other' | 'commits' | `site:${string}` | 'sites' | 'mail';

function groupOf(t: LinkTarget, ctx: LinkContext): GroupKey {
  switch (t.kind) {
    case 'ref':
      return same(t.owner, ctx.pr.owner) && same(t.repo, ctx.pr.repo) ? 'repo' : 'other';
    case 'commit':
      return 'commits';
    case 'web':
      return `site:${t.site}`;
    case 'mail':
      return 'mail';
  }
}

function groupLabel(key: GroupKey): string {
  if (key === 'repo') return 'GitHub · this repository';
  if (key === 'other') return 'GitHub · other repositories';
  if (key === 'commits') return 'Commits';
  if (key === 'mail') return 'Email';
  if (key === 'sites') return 'Other sites';
  return key.slice('site:'.length);
}

const byRank = (a: GroupKey, b: GroupKey) => {
  const [ra, na] = groupRank(a);
  const [rb, nb] = groupRank(b);
  return ra - rb || na.localeCompare(nb);
};

/** Groups in display order: GitHub here, GitHub elsewhere, commits, sites by name, other sites, email. */
function groupRank(key: GroupKey): [number, string] {
  if (key === 'repo') return [0, ''];
  if (key === 'other') return [1, ''];
  if (key === 'commits') return [2, ''];
  if (key === 'sites') return [4, ''];
  if (key === 'mail') return [5, ''];
  return [3, key];
}

function inThisRepo(t: { owner: string; repo: string }, ctx: LinkContext): boolean {
  return same(t.owner, ctx.pr.owner) && same(t.repo, ctx.pr.repo);
}

function label(t: LinkTarget, ctx: LinkContext): string {
  switch (t.kind) {
    case 'ref':
      return inThisRepo(t, ctx) ? `#${t.number}` : `${t.owner}/${t.repo}#${t.number}`;
    case 'commit':
      return inThisRepo(t, ctx) ? t.sha.slice(0, 7) : `${t.owner}/${t.repo}@${t.sha.slice(0, 7)}`;
    case 'web':
      return t.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    case 'mail':
      return t.address;
  }
}

function known(t: Extract<LinkTarget, { kind: 'ref' }>, ctx: LinkContext): KnownRef | null {
  return ctx.known.find((k) => k.number === t.number && same(k.owner, t.owner) && same(k.repo, t.repo)) ?? null;
}

function title(t: LinkTarget, ctx: LinkContext): string | null {
  if (t.kind === 'ref') return known(t, ctx)?.title ?? null;
  if (t.kind === 'commit' && inThisRepo(t, ctx)) return ctx.commits.find((c) => same(c.sha, t.sha))?.title ?? null;
  return null;
}

/**
 * Where following a target goes. A pull request in this repository opens
 * in changelist (the viewer can read it); one of this pull request's
 * commits opens its diff (the Files view from its parent to it).
 * Everything else opens on its own site, including a pull request in
 * another repository: changelist does not know whether the viewer may
 * read it, and would show "Pull request not found" if not.
 */
function open(t: LinkTarget, ctx: LinkContext): Open {
  const web = ctx.githubWebUrl.replace(/\/+$/, '');
  switch (t.kind) {
    case 'ref':
      if (t.type === 'pull' && inThisRepo(t, ctx)) return { kind: 'app', path: prPath(t.owner, t.repo, t.number) };
      return { kind: 'external', url: `${web}/${t.owner}/${t.repo}/${t.type === 'pull' ? 'pull' : 'issues'}/${t.number}` };
    case 'commit':
      if (inThisRepo(t, ctx) && ctx.commits.some((c) => same(c.sha, t.sha))) {
        return { kind: 'app', path: commitPath(ctx.pr, t.sha) };
      }
      return { kind: 'external', url: `${web}/${t.owner}/${t.repo}/commit/${t.sha}` };
    case 'web':
      return { kind: 'external', url: t.url };
    case 'mail':
      return { kind: 'external', url: t.url };
  }
}

const placeKey = (m: Mention) => {
  const p = m.place;
  return `${p.kind}:${p.kind === 'thread' ? p.comment : p.kind === 'description' ? '' : p.id}:${m.author}:${m.at}`;
};

/**
 * The index of every link in `sources`: one entry per target, with each
 * place that mentions it once, grouped (see `groupRank`). With a `memo`,
 * a source read before with the same key is not read again. A site with
 * several links has a group of its own; sites linked once share "Other
 * sites", by site name. Issue and pull request references sort by
 * repository and number; other entries keep the order of their first
 * mention.
 */
export function linksIndex(sources: readonly LinkSource[], ctx: LinkContext, memo?: LinkMemo): LinkGroup[] {
  const entries = new Map<string, { target: LinkTarget; mentions: Mention[]; seen: Set<string> }>();
  for (const s of sources) {
    const mention: Mention = { place: s.place, author: s.author, at: s.at };
    const where = placeKey(mention);
    let targets: LinkTarget[];
    try {
      targets = memo ? memo.targets(s, ctx, () => extractLinks(s.html, ctx)) : extractLinks(s.html, ctx);
    } catch {
      // One unreadable source loses its own links, never the index.
      continue;
    }
    for (const t of targets) {
      const key = targetKey(t, ctx);
      let e = entries.get(key);
      if (!e) entries.set(key, (e = { target: t, mentions: [], seen: new Set() }));
      else if (t.kind === 'ref' && e.target.kind === 'ref' && e.target.type === null) e.target = { ...e.target, type: t.type };
      // The longest SHA given opens the commit.
      else if (t.kind === 'commit' && e.target.kind === 'commit' && t.sha.length > e.target.sha.length) e.target = t;
      if (!e.seen.has(where)) {
        e.seen.add(where);
        e.mentions.push(mention);
      }
    }
  }
  const groups = new Map<GroupKey, LinkEntry[]>();
  for (const [key, e] of entries) {
    let target = e.target;
    // GitHub's own word on what a reference is wins over a guess from its URL.
    if (target.kind === 'ref') target = { ...target, type: known(target, ctx)?.type ?? target.type };
    const g = groupOf(target, ctx);
    const list = groups.get(g) ?? [];
    list.push({ key, target, label: label(target, ctx), title: title(target, ctx), open: open(target, ctx), mentions: e.mentions });
    groups.set(g, list);
  }
  const refOrder = (a: LinkEntry, b: LinkEntry) =>
    a.target.kind === 'ref' && b.target.kind === 'ref'
      ? `${a.target.owner}/${a.target.repo}`.toLowerCase().localeCompare(`${b.target.owner}/${b.target.repo}`.toLowerCase()) || a.target.number - b.target.number
      : 0;
  for (const key of [...groups.keys()].sort(byRank)) {
    const list = groups.get(key)!;
    if (!key.startsWith('site:') || list.length > 1) continue;
    groups.delete(key);
    groups.set('sites', [...(groups.get('sites') ?? []), ...list]);
  }
  return [...groups]
    .sort(([a], [b]) => byRank(a, b))
    .map(([key, list]) => ({ key, label: groupLabel(key), entries: list.sort(refOrder) }));
}

/**
 * The first `limit` entries, across groups in order, and how many more
 * there are. Groups left empty are dropped.
 */
export function collapse(groups: LinkGroup[], limit: number): { groups: LinkGroup[]; hidden: number } {
  const total = groups.reduce((n, g) => n + g.entries.length, 0);
  if (total <= limit) return { groups, hidden: 0 };
  const out: LinkGroup[] = [];
  let left = limit;
  for (const g of groups) {
    if (left <= 0) break;
    out.push({ ...g, entries: g.entries.slice(0, left) });
    left -= g.entries.length;
  }
  return { groups: out, hidden: total - limit };
}

// ---------------------------------------------------------------- names

/** Where a mention is, briefly: a chip's text. */
export function placeShort(p: Place): string {
  switch (p.kind) {
    case 'description':
      return 'description';
    case 'comment':
      return 'comment';
    case 'review':
      return 'review';
    case 'thread': {
      const file = p.thread.path.split('/').pop() ?? p.thread.path;
      return p.thread.line && p.thread.subjectType !== 'FILE' ? `${file}:${p.thread.line}` : file;
    }
  }
}

/** What a mention's chip shows: who, where and when (`when`, e.g. "2d ago"). */
export function mentionText(m: Mention, when: string): string {
  return `${m.author} · ${placeShort(m.place)} · ${when}`;
}

/**
 * A mention's accessible name: the chip's text, so a speech user can say
 * what they see, then where in full ("mara · comment · 2d ago, mentioned
 * in a comment").
 */
export function mentionName(m: Mention, when: string): string {
  const p = m.place;
  const where =
    p.kind === 'description'
      ? 'the description'
      : p.kind === 'comment'
        ? 'a comment'
        : p.kind === 'review'
          ? 'a review'
          : `${p.reply ? 'a reply' : 'a line comment'} on ${p.thread.path}${p.thread.line && p.thread.subjectType !== 'FILE' ? ` line ${p.thread.line}` : ''}`;
  return `${mentionText(m, when)}, mentioned in ${where}`;
}
